/**
 * Eğitim & Yardım Merkezi — Tek merkez sayfası (/egitim).
 *
 * Alt bileşenler:
 *  - LearningCenter (default export) — kategoriler + arama + Yenilikler tab
 *  - HelpButton                       — her sayfada sağ üst yardım ikonu
 *  - WhatsNewBanner                   — login sonrası "Yeni Özellik" bildirimi
 *  - startScreenTour(steps)           — driver.js ile adım adım tur
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  BookOpen, Search, Sparkles, HelpCircle, PlayCircle, ArrowRight,
  Loader2, ExternalLink, X,
} from "lucide-react";
import { toast } from "sonner";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { renderMarkdown, isSafeInternalRoute } from "@/lib/markdown";

// ==========================================================================
// Public helpers
// ==========================================================================
export async function startScreenTour(tourId) {
  try {
    const { data } = await api.get(`/learning/tours/${tourId}`);
    const steps = (data.steps || []).map((s) => ({
      element: s.selector,
      popover: { title: s.title, description: s.description, side: s.side || "bottom" },
    }));
    if (!steps.length) { toast.info("Bu eğitim için tur adımı tanımlı değil"); return; }
    const d = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      allowClose: true,
      steps,
      doneBtnText: "Bitir",
      nextBtnText: "İleri",
      prevBtnText: "Geri",
      closeBtnText: "Atla",
    });
    d.drive();
  } catch (e) { toast.error(formatApiError(e)); }
}


function ArticleView({ article, onClose, onOpenRoute, onStartTour, onOpenCenter }) {
  const html = useMemo(() => renderMarkdown(article.content_md || ""), [article.content_md]);

  const handleRoute = () => {
    const r = article.related_route;
    if (!isSafeInternalRoute(r)) {
      toast.error("Güvensiz yönlendirme engellendi.");
      return;
    }
    onClose();
    onOpenRoute?.(r);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="article-view">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <BookOpen className="w-5 h-5 text-blue-400" /> {article.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 flex items-center gap-2 mt-1">
            <Badge className="bg-slate-900 border-slate-700 text-slate-300 rounded-sm">v{article.version}</Badge>
            <Badge className="bg-slate-900 border-slate-700 text-slate-400 rounded-sm">{article.module}</Badge>
            <span>Son güncelleme: {new Date(article.updated_at).toLocaleDateString("tr-TR")}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm text-slate-200" dangerouslySetInnerHTML={{ __html: html }} />
        <DialogFooter className="gap-2">
          {article.tour_id && (
            <Button variant="outline" className="border-emerald-800 text-emerald-300 rounded-sm"
              onClick={() => {
                // FAZ B: tour başlatmadan önce related_route'a git (selector'ların bulunması için)
                onClose();
                if (article.related_route && isSafeInternalRoute(article.related_route)) {
                  onOpenRoute(article.related_route);
                  setTimeout(() => onStartTour?.(article.tour_id), 800);
                } else {
                  onStartTour?.(article.tour_id);
                }
              }} data-testid="article-start-tour">
              <PlayCircle className="w-4 h-4 mr-1" /> Bana Adım Adım Göster
            </Button>
          )}
          {article.related_route && isSafeInternalRoute(article.related_route) && (
            <Button variant="outline" className="border-blue-800 text-blue-300 rounded-sm"
              onClick={handleRoute} data-testid="article-open-route">
              <ExternalLink className="w-4 h-4 mr-1" /> İlgili Ekrana Git
            </Button>
          )}
          {onOpenCenter && (
            <Button variant="outline" className="border-blue-800 text-blue-300 rounded-sm"
              onClick={() => { onClose(); onOpenCenter(); }} data-testid="article-open-center">
              <BookOpen className="w-4 h-4 mr-1" /> Tüm Yardım Merkezi
            </Button>
          )}
          <Button variant="outline" className="border-slate-700 rounded-sm" onClick={onClose}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ArticleView };


// ==========================================================================
// Main page /egitim
// ==========================================================================
export default function LearningCenter() {
  const nav = useNavigate();
  const [tab, setTab] = useState("articles");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [{ data: a }, { data: n }] = await Promise.all([
        api.get("/learning/articles", { params: q ? { q } : {} }),
        api.get("/learning/whats-new"),
      ]);
      setItems(a?.items || []);
      setNews(n?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    for (const a of items) { (g[a.module] = g[a.module] || []).push(a); }
    return g;
  }, [items]);

  const KIND_BADGE = {
    yeni: "bg-emerald-950 text-emerald-300 border-emerald-800",
    gelistirildi: "bg-blue-950 text-blue-300 border-blue-800",
    degisti: "bg-amber-950 text-amber-300 border-amber-800",
  };
  const KIND_LABEL = { yeni: "YENİ", gelistirildi: "GELİŞTİRİLDİ", degisti: "DEĞİŞTİ" };

  // FAZ B: Pedagojik modül sırası (ERP menüsüne paralel) + Türkçe label + emoji.
  // Yalnızca burada tanımlı olan modüller "bilinen" sayılır; diğerleri "Diğer" grubunda toplanır.
  const MODULE_META = {
    dashboard:    { label: "Kontrol Paneli",       order: 1 },
    customers:    { label: "Müşteriler & CRM",     order: 2 },
    products:     { label: "Ürünler & Reçete",     order: 3 },
    inventory:    { label: "Stok Yönetimi",        order: 4 },
    warehouse:    { label: "Depolar",              order: 5 },
    wizard:       { label: "Sipariş Sihirbazı",    order: 6 },
    orders:       { label: "Siparişler & Teklifler", order: 7 },
    operations:   { label: "Üretim & Operasyon",   order: 8 },
    suppliers:    { label: "Tedarikçiler",         order: 9 },
    purchases:    { label: "Satın Alma",           order: 10 },
    shipments:    { label: "Sevkiyat",             order: 11 },
    finance:      { label: "Finans & Cari",        order: 12 },
    reports:      { label: "Raporlar",             order: 13 },
    employees:    { label: "İnsan Kaynakları",     order: 14 },
    pdks:         { label: "PDKS",                 order: 15 },
    puantaj:      { label: "Puantaj",              order: 16 },
    maas:         { label: "Maaş",                 order: 17 },
    tasks:        { label: "Görev Yönetimi",       order: 18 },
    whatsapp:     { label: "WhatsApp",             order: 19 },
    settings:     { label: "Sistem & Ayarlar",     order: 20, adminOnly: true },
    learning:     { label: "Yardım Merkezi",       order: 21 },
  };
  const groupedOrdered = useMemo(() => {
    const entries = Object.entries(grouped);
    entries.sort(([a], [b]) => (MODULE_META[a]?.order ?? 999) - (MODULE_META[b]?.order ?? 999));
    return entries;
  }, [grouped]);

  return (
    <div>
      <PageHeader title="Eğitim & Yardım Merkezi" subtitle="Modül eğitimleri · Yenilikler · Ekran turları" />
      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
        {/* FAZ B: Hero arama */}
        <div className="rounded-sm border border-slate-800 bg-gradient-to-b from-slate-900/60 to-slate-950/40 p-6 sm:p-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-blue-400 font-mono">ACUTO 360 · YARDIM MERKEZİ</div>
          <h2 className="text-2xl sm:text-4xl font-bold text-slate-100 mt-3">Nasıl yardımcı olabiliriz?</h2>
          <p className="text-sm text-slate-400 mt-2 max-w-lg mx-auto">
            Modül adı, işlem veya soru yazın. Örn: <span className="text-slate-300">"yeni depo nasıl açılır"</span>,
            <span className="text-slate-300"> "reçete"</span>, <span className="text-slate-300">"sipariş kaydet"</span>.
          </p>
          <div className="relative max-w-2xl mx-auto mt-6">
            <Search className="w-5 h-5 absolute left-3 top-3 text-slate-500" />
            <Input
              data-testid="learning-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Yardım ara…"
              className="bg-slate-950 border-slate-700 rounded-sm pl-11 h-11 text-base"
              autoFocus
            />
          </div>
          {q && !loading && (
            <div className="text-xs text-slate-500 mt-3 font-mono" data-testid="learning-search-count">
              {items.length} sonuç bulundu
            </div>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-muted/40 border border-border">
            <TabsTrigger value="articles" className="rounded-sm" data-testid="learning-tab-articles">
              <BookOpen className="w-3.5 h-3.5 mr-1" /> Eğitimler ({items.length})
            </TabsTrigger>
            <TabsTrigger value="whats-new" className="rounded-sm" data-testid="learning-tab-news">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Yenilikler ({news.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="articles" className="mt-4 space-y-4">
            {loading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…</div>}
            {!loading && items.length === 0 && (
              <Card className="p-8 text-center text-slate-500 border-slate-800 bg-slate-950/40">
                Bu kritere uyan eğitim bulunamadı. Yönetici olarak Ayarlar → Eğitim'den yeni eğitim ekleyebilirsiniz.
              </Card>
            )}
            {Object.keys(grouped).length > 0 && (
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                {Object.keys(grouped).length} kategori · {items.length} rehber
              </div>
            )}
            {groupedOrdered.map(([mod, arts]) => {
              const meta = MODULE_META[mod] || { label: mod };
              return (
                <div key={mod} data-testid={`module-block-${mod}`}>
                  <div className="flex items-baseline gap-2 mb-2">
                    <div className="text-sm uppercase tracking-widest text-blue-400 font-mono font-semibold">
                      {meta.label}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">{arts.length} rehber</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {arts.map((a) => (
                      <Card key={a.id} onClick={() => setActive(a)}
                        className="p-3 border-slate-800 bg-slate-950/40 hover:bg-slate-900/60 hover:border-blue-800 cursor-pointer transition"
                        data-testid={`article-card-${a.slug}`}>
                        <div className="flex items-start gap-2">
                          <BookOpen className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-100 font-semibold truncate">{a.title}</div>
                            <div className="text-[10px] text-slate-500 font-mono mt-1 flex items-center gap-1.5 flex-wrap">
                              <span>v{a.version}</span>
                              <span>·</span>
                              <span>{new Date(a.updated_at).toLocaleDateString("tr-TR")}</span>
                              {a.related_route && <>
                                <span>·</span>
                                <span className="text-blue-400">{a.related_route}</span>
                              </>}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="whats-new" className="mt-4 space-y-2">
            {news.length === 0 && <Card className="p-6 text-center text-slate-500 border-slate-800">Henüz duyuru yok.</Card>}
            {news.map((n) => (
              <Card key={n.id} className="p-3 border-slate-800 bg-slate-950/40" data-testid={`whats-new-${n.id}`}>
                <div className="flex items-start gap-2">
                  <Badge className={`rounded-sm text-[9px] ${KIND_BADGE[n.kind] || KIND_BADGE.yeni}`}>{KIND_LABEL[n.kind] || n.kind}</Badge>
                  <div className="flex-1">
                    <div className="text-sm text-slate-100 font-semibold">{n.title}</div>
                    <div className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{n.description}</div>
                    <div className="text-[10px] text-slate-500 mt-2 font-mono">
                      {new Date(n.date).toLocaleDateString("tr-TR")}
                      {n.modules?.length > 0 && <> · {n.modules.join(", ")}</>}
                    </div>
                    {n.article_slug && (
                      <Button variant="link" size="sm" className="text-blue-400 h-6 px-0 mt-1"
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/learning/articles/${n.article_slug}`);
                            setActive(data);
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}>
                        Nasıl Kullanılır? →
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {active && (
        <ArticleView article={active} onClose={() => setActive(null)}
          onOpenRoute={(r) => nav(r)}
          onStartTour={(tid) => startScreenTour(tid)} />
      )}
    </div>
  );
}


// FAZ B: route → module fallback map.
// Yardım butonuna basılan sayfada makale yoksa ilgili modülün genel makalelerini önerir.
const ROUTE_MODULE_MAP = {
  "/": "dashboard",
  "/musteri": "customers",
  "/urunler": "products",
  "/urun": "products",
  "/varyantlar": "products",
  "/stok": "inventory",
  "/depolar": "warehouse",
  "/siparis-sihirbazi": "wizard",
  "/satislar": "orders",
  "/teklifler": "orders",
  "/uretim": "operations",
  "/tedarikci": "suppliers",
  "/satinalma": "purchases",
  "/satin-alma-ihtiyaclari": "purchases",
  "/sevkiyat": "shipments",
  "/cari": "finance",
  "/cek-senet": "finance",
  "/raporlar": "reports",
  "/operasyonlar": "operations",
  "/calisan": "employees",
  "/pdks": "pdks",
  "/puantaj": "puantaj",
  "/maas": "maas",
  "/gorevler": "tasks",
  "/whatsapp": "whatsapp",
  "/ayarlar": "settings",
  "/egitim": "learning",
};

function pathToModule(pathname) {
  if (!pathname) return null;
  if (ROUTE_MODULE_MAP[pathname]) return ROUTE_MODULE_MAP[pathname];
  // Prefix-fallback (örn: /pdks/panel → pdks)
  for (const [route, mod] of Object.entries(ROUTE_MODULE_MAP)) {
    if (route !== "/" && pathname.startsWith(route + "/")) return mod;
  }
  return null;
}

// ==========================================================================
// HelpButton — every screen header
// ==========================================================================
export function HelpButton() {
  const loc = useLocation();
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    try {
      setBusy(true);
      // 1) Önce route-based tam eşleşme
      let { data } = await api.get("/learning/articles", { params: { route: loc.pathname } });
      let arr = data?.items || [];
      if (arr.length === 0) {
        // 2) Fallback → aynı modülün tüm makaleleri
        const mod = pathToModule(loc.pathname);
        if (mod) {
          const r2 = await api.get("/learning/articles", { params: { module: mod } });
          arr = r2.data?.items || [];
        }
      }
      if (arr.length === 0) {
        toast.info("Bu ekran için henüz eğitim yok. Yardım Merkezi'ne yönlendiriyorum…");
        nav("/egitim");
        return;
      }
      // Route ile birebir eşleşen varsa onu göster; yoksa modülün ilkini
      const exact = arr.find((a) => a.related_route === loc.pathname);
      setArticle(exact || arr[0]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={open} disabled={busy}
        className="border-slate-700 rounded-sm text-slate-300 hover:text-blue-300 hover:border-blue-700"
        data-testid="help-button" title="Bu ekranı nasıl kullanırım?">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HelpCircle className="w-3.5 h-3.5 mr-1" />}
        Yardım
      </Button>
      {article && <ArticleView article={article} onClose={() => setArticle(null)}
        onOpenRoute={(r) => nav(r)}
        onOpenCenter={() => nav("/egitim")}
        onStartTour={(tid) => startScreenTour(tid)} />}
    </>
  );
}


// ==========================================================================
// WhatsNewBanner — login sonrası bir kez
// ==========================================================================
export function WhatsNewBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/learning/whats-new", { params: { unseen_only: true } });
        setItems(data?.items || []);
      } catch { }
    })();
  }, []);

  if (dismissed || items.length === 0) return null;
  const top = items[0];

  const markSeenAll = async () => {
    try { await Promise.all(items.map((i) => api.post(`/learning/whats-new/${i.id}/mark-seen`))); } catch { }
    setDismissed(true);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-slate-950 border border-blue-800 rounded-sm shadow-2xl p-3" data-testid="whats-new-banner">
      <div className="flex items-start gap-2">
        <Sparkles className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-blue-300 font-mono">ERP'de Yeni Bir Özellik Var</div>
          <div className="text-sm text-slate-100 font-semibold mt-1">{top.title}</div>
          <div className="text-xs text-slate-400 mt-1 line-clamp-2">{top.description}</div>
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-sm h-7 text-xs"
              onClick={() => { nav("/egitim"); markSeenAll(); }} data-testid="whats-new-open">
              Nasıl Kullanılır? →
            </Button>
            <Button size="sm" variant="ghost" className="text-slate-400 h-7 text-xs" onClick={markSeenAll}
              data-testid="whats-new-dismiss">
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
      {items.length > 1 && <div className="text-[10px] text-slate-500 mt-2 border-t border-slate-800 pt-1">+{items.length - 1} yeni duyuru daha</div>}
    </div>
  );
}
